import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Select from 'components/Select';
import { FilePdfOutlined } from '@ant-design/icons';
import { postObtenerReporte } from 'api/reporteador';
import Previewer from 'components/Previewer';
import getAllPages from 'utils/getAllPages';
import { getPlantillasDeTramitePortal } from 'api/tramites';
import { DATE_FRONTEND_FORMAT, dateFormatterToReporteador } from 'utils/formatValues';
import { getUnidadesDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';
import moment from 'moment';

export const permissionConfig = {
  mainPermission: 'reportes_tramite',
  required: ['view_usuario', 'view_unidadderecaudacion'],
};

const data = [
  [10061, 'Relacion de Tramites Cobrados por Tipo '],
  [1045, 'Reporte General de Trámites '],
].map(([id, nombre]) => ({ id, nombre }));

const ReportesDeTramites = () => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visiblePreviewer, setVisiblePreviewer] = useState(false);
  const [base64, setBase64] = useState('');
  const [plantillas, setPlantillas] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [selectedRowKey] = selectedRowKeys;

  const nombreReporte = data.find((item) => item.id === selectedRowKey)?.nombre;

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const _unidades = await getUnidadesDeRecaudacion();
      const _plantillas = await getAllPages(getPlantillasDeTramitePortal);
      if (mounted) {
        setUnidades(_unidades);
        setPlantillas(_plantillas);
        setLoading(false);
      }
    };

    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setBase64('');
  };

  const formatPostValues = (ID, values) => {
    const keys = Object.keys(values);
    const Parametros = keys.map((Nombre) => {
      const fields = {
        fechainicio: () => dateFormatterToReporteador(values[Nombre]),
        fechafinal: () => dateFormatterToReporteador(values[Nombre], false),
      };
      const dataType = {
        plantilla_de_tramite_id: 'Entero',
        fechainicio: 'Fecha',
        fechafinal: 'Fecha',
        unidadrecaudacion: 'Entero',
      };
      const Valor = (fields[Nombre] && fields[Nombre]()) || values[Nombre] || null;
      const TipoParametro = dataType?.[Nombre] || 'Texto';
      return {
        Nombre,
        TipoParametro,
        Valor,
      };
    }).filter((e) => e.Valor !== null);
    return { Parametros, ID };
  };

  const onFinish = async () => {
    setLoading(true);
    const _values = form.getFieldsValue();
    const [key] = selectedRowKeys;

    if (_values.fechas) {
      [_values.fechainicio, _values.fechafinal] = _values.fechas;
      delete _values.fechas;
    }
    const values = formatPostValues(key, _values);
    const args = [values];
    const result = await postObtenerReporte(...args);
    if (result) {
      onCancel();
      setBase64(result);
      setVisiblePreviewer(true);
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickConsultar = () => {
    form.resetFields();
    setVisible(true);
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Nombre del reporte',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 350,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          customActions={[{
            icon: <FilePdfOutlined />,
            text: 'Consultar',
            onClick: onClickConsultar,
          }]}
          rowKey="id"
          mobileColIndex={0}
        />
        <Modal
          open={visible}
          footer={null}
          closable={false}
          maskClosable={false}
          width={700}
          title={(
            <FormSubmitControls
              onFinish={form.submit}
              onCancel={onCancel}
              loading={loading}
              label={nombreReporte}
              onFinishText="Ejecutar"
            />
          )}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
          >
            <Row gutter={10}>
              {selectedRowKey === 10061 && (
                <Col span={24}>
                  <Form.Item
                    label="Tipo de trámite"
                    rules={rules.required}
                    name="plantilla_de_tramite_id"
                  >
                    <Select dataSource={plantillas} labelProp="nombre" disabled={loading} />
                  </Form.Item>
                </Col>
              )}
              {selectedRowKey === 1045 && (
                <>
                  <Col span={24}>
                    <Form.Item
                      label="Fecha Inicial y Final"
                      rules={rules.required}
                      name="fechas"
                    >
                      <DatePicker.RangePicker
                        placeholder=""
                        format={DATE_FRONTEND_FORMAT}
                        disabledDate={(current) => current && current > moment().endOf('day')}
                        disabled={loading}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label="Dependencia"
                      rules={rules.required}
                      name="unidadrecaudacion"
                    >
                      <Select dataSource={unidades} disabled={loading} />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Modal>
      </Spin>
      <Previewer
        visible={visiblePreviewer}
        base64={base64}
        setVisible={(value) => {
          setVisiblePreviewer(value);
          onCancel();
        }}
        allowSend
        isImage={false}
        allowDownload
        fileName={nombreReporte}
        // _visibleInput
      />
    </Row>
  );
};

export default ReportesDeTramites;
